import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Popover } from 'antd';
import type { SurveyPoint } from './typesCore';
import {
  DEFAULT_NATURAL_FRACTURE_SET,
  customPreviewSegments,
  generateCustomNaturalFractures,
  geologyPreviewSegments,
  interpolateSurveyXY,
  normalizeNaturalFractureDesign,
  type NaturalFractureDesignDTO,
  type NaturalFracturePreviewSegment,
  type NaturalFractureSetDefinition,
  type RawGeologyNaturalFracture,
} from './naturalFractureDesign';

const control: React.CSSProperties = {
  width: '100%', minWidth: 68, boxSizing: 'border-box', border: '1px solid #C9D0D8',
  borderRadius: 4, padding: '7px 8px', fontSize: 13, lineHeight: 1.35, color: '#1F3145', background: '#fff',
};
const button: React.CSSProperties = {
  border: '1px solid #B9C3CE', borderRadius: 4, background: '#F7F8FA',
  color: '#2E4358', padding: '7px 12px', fontSize: 12, lineHeight: 1.2, cursor: 'pointer',
};

const cloneDesign = (value: NaturalFractureDesignDTO): NaturalFractureDesignDTO => JSON.parse(JSON.stringify(value));

function ClickHelp({ content, label = '查看说明' }: { content: React.ReactNode; label?: string }) {
  return <Popover trigger="click" placement="bottomLeft" content={<div style={{ maxWidth: 320, lineHeight: 1.65, color: '#40566B' }}>{content}</div>}>
    <button type="button" aria-label={label} style={{
      width: 18, height: 18, padding: 0, borderRadius: '50%', border: '1px solid #9BAABC',
      background: '#fff', color: '#65788C', fontSize: 11, lineHeight: '16px', cursor: 'pointer',
    }}>?</button>
  </Popover>;
}

function NaturalFracture2DPreview({
  design, survey, perforationMds,
}: { design: NaturalFractureDesignDTO; survey: SurveyPoint[]; perforationMds: number[] }) {
  const boundary = design.design_metadata.boundary;
  const segments = useMemo(() => {
    const geology = design.source_mode === 'GEOLOGY'
      ? geologyPreviewSegments(design.raw_geology_fractures, survey) : [];
    const custom = design.source_mode === 'CUSTOM'
      ? customPreviewSegments(design.custom_fractures) : [];
    return [...geology, ...custom];
  }, [design, survey]);
  const customSetColors = useMemo(() => {
    const palette = ['#B45F3D', '#346E9D', '#7A5A9E', '#3E8061', '#A77722', '#A4496E'];
    return new Map(design.fracture_sets.map((set, index) => [set.id, palette[index % palette.length]]));
  }, [design.fracture_sets]);
  const fitted = useMemo(() => ({
    x: boundary.centerX - boundary.widthM * 0.56,
    y: -boundary.centerY - boundary.heightM * 0.56,
    width: boundary.widthM * 1.12,
    height: boundary.heightM * 1.12,
  }), [boundary]);
  const [view, setView] = useState(fitted);
  const [hovered, setHovered] = useState<NaturalFracturePreviewSegment | null>(null);
  const [showGeology, setShowGeology] = useState(true);
  const [showCustom, setShowCustom] = useState(true);
  const drag = useRef<{ x: number; y: number; viewX: number; viewY: number } | null>(null);
  useEffect(() => setView(fitted), [fitted]);
  const wellPoints = survey
    .filter((point) => Number.isFinite(point.ew) && Number.isFinite(point.ns))
    .map((point) => `${point.ew},${-point.ns}`).join(' ');
  const perfs = perforationMds.map((md) => interpolateSurveyXY(survey, md)).filter(Boolean) as Array<{ x: number; y: number; z: number }>;
  const pan = (event: React.PointerEvent<SVGSVGElement>) => {
    if (!drag.current) return;
    const rect = event.currentTarget.getBoundingClientRect();
    setView((current) => ({
      ...current,
      x: drag.current!.viewX - (event.clientX - drag.current!.x) / rect.width * current.width,
      y: drag.current!.viewY - (event.clientY - drag.current!.y) / rect.height * current.height,
    }));
  };
  return <div style={{ height: '100%', minHeight: 440, display: 'flex', flexDirection: 'column', border: '1px solid #C9D0D8', borderRadius: 5, background: '#FCFCFD', overflow: 'hidden' }}>
    <div style={{ minHeight: 42, padding: '0 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #D7DCE2', background: '#F2F4F6' }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 700, color: '#23384D' }}>
        天然裂缝预览
        <ClickHelp content={<>内部坐标采用 X 东向、Y 北向。滚轮缩放，按住并拖动可平移视图。</>} label="查看预览操作说明" />
      </span>
      <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, fontWeight: 400 }}>
        {design.source_mode === 'GEOLOGY' && <label><input type="checkbox" checked={showGeology} onChange={(event) => setShowGeology(event.target.checked)} /> 地质裂缝</label>}
        {design.source_mode === 'CUSTOM' && <label><input type="checkbox" checked={showCustom} onChange={(event) => setShowCustom(event.target.checked)} /> 自定义裂缝</label>}
        <button type="button" onClick={() => setView(fitted)} style={button}>适配视图</button>
      </span>
    </div>
    <svg
      role="img" aria-label="二维天然裂缝预览" style={{ flex: 1, width: '100%', cursor: drag.current ? 'grabbing' : 'grab' }}
      viewBox={`${view.x} ${view.y} ${view.width} ${view.height}`}
      onWheel={(event) => {
        event.preventDefault();
        const factor = event.deltaY > 0 ? 1.15 : 0.87;
        const rect = event.currentTarget.getBoundingClientRect();
        const px = view.x + (event.clientX - rect.left) / rect.width * view.width;
        const py = view.y + (event.clientY - rect.top) / rect.height * view.height;
        setView({ x: px - (px - view.x) * factor, y: py - (py - view.y) * factor, width: view.width * factor, height: view.height * factor });
      }}
      onPointerDown={(event) => { drag.current = { x: event.clientX, y: event.clientY, viewX: view.x, viewY: view.y }; event.currentTarget.setPointerCapture(event.pointerId); }}
      onPointerMove={pan}
      onPointerUp={(event) => { drag.current = null; event.currentTarget.releasePointerCapture(event.pointerId); }}
      onPointerLeave={() => { drag.current = null; }}
    >
      <rect x={boundary.centerX - boundary.widthM / 2} y={-boundary.centerY - boundary.heightM / 2}
        width={boundary.widthM} height={boundary.heightM} fill="#F7F8FA" stroke="#8795A5" strokeWidth={view.width / 700} />
      {wellPoints && <polyline points={wellPoints} fill="none" stroke="#26394D" strokeWidth={view.width / 260} />}
      {perfs.map((point, index) => <circle key={index} cx={point.x} cy={-point.y} r={view.width / 210} fill="#B3533B" />)}
      {segments.filter((segment) => segment.source === 'GEOLOGY' ? showGeology : showCustom).map((segment) => <line key={segment.id}
        x1={segment.x1} y1={-segment.y1} x2={segment.x2} y2={-segment.y2}
        stroke={segment.source === 'GEOLOGY' ? '#52697D' : customSetColors.get(segment.setId ?? '') ?? '#9A674E'}
        strokeDasharray={segment.source === 'GEOLOGY' ? undefined : `${view.width / 180} ${view.width / 300}`}
        strokeOpacity={segment.source === 'GEOLOGY' ? 0.68 : 0.82}
        strokeWidth={view.width / 520}
        onPointerEnter={(event) => { event.stopPropagation(); setHovered(segment); }}
        onPointerLeave={() => setHovered(null)}
      />)}
    </svg>
    {hovered && <div style={{ minHeight: 30, borderTop: '1px solid #D7DCE2', padding: '6px 10px', fontSize: 12, color: '#26394D', textAlign: 'right' }}>
      {hovered.id} · {hovered.lengthM.toFixed(1)} m · {hovered.orientationDeg.toFixed(1)}°
    </div>}
  </div>;
}

export default function NaturalFractureDesignDialog({
  open, value, rawGeologyFractures, survey, perforationMds, geologyLoading,
  onApply, onCancel,
}: {
  open: boolean;
  value: NaturalFractureDesignDTO;
  rawGeologyFractures: RawGeologyNaturalFracture[];
  survey: SurveyPoint[];
  perforationMds: number[];
  geologyLoading: boolean;
  onApply: (value: NaturalFractureDesignDTO) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(() => cloneDesign(value));
  useEffect(() => {
    if (!open) return;
    const next = normalizeNaturalFractureDesign(cloneDesign(value));
    next.raw_geology_fractures = rawGeologyFractures.map((fracture) => ({ ...fracture }));
    setDraft(next);
  }, [open, value, rawGeologyFractures]);
  const usesCustom = draft.source_mode === 'CUSTOM';
  const usesGeology = draft.source_mode === 'GEOLOGY';
  const updateSet = (id: string, patch: Partial<NaturalFractureSetDefinition>) => setDraft((current) => ({
    ...current,
    fracture_sets: current.fracture_sets.map((set) => set.id === id ? { ...set, ...patch } : set),
  }));
  const regenerate = () => setDraft((current) => {
    const seed = current.design_metadata.random_seed + 1;
    return {
      ...current,
      custom_fractures: generateCustomNaturalFractures(
        current.fracture_sets, current.design_metadata.boundary, seed, current.custom_fractures,
      ),
      design_metadata: { ...current.design_metadata, random_seed: seed },
    };
  });
  const addSet = () => setDraft((current) => {
    const nextIndex = Math.max(0, ...current.fracture_sets.map((set, index) => {
      const match = /^(?:自定义裂缝组|Set)\s*(\d+)$/i.exec(set.name.trim());
      return match ? Number(match[1]) : index + 1;
    })) + 1;
    return {
      ...current,
      fracture_sets: [...current.fracture_sets, {
        ...DEFAULT_NATURAL_FRACTURE_SET,
        id: `NF-SET-${Date.now()}-${nextIndex}`,
        name: `Set${nextIndex}`,
      }],
    };
  });
  return <Modal
    open={open} width="92vw" centered footer={null} destroyOnClose={false}
    title={<span style={{ fontSize: 17, color: '#20364B' }}>天然裂缝设计</span>}
    onCancel={onCancel}
    styles={{ body: { height: '80vh', minHeight: 620, padding: 0, overflow: 'hidden' } }}
  >
    <div style={{ height: '100%', display: 'grid', gridTemplateRows: '48px minmax(0, 1fr) 50px', color: '#26394D', fontSize: 13 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 14px', borderBottom: '1px solid #CDD3DA', background: '#F5F6F8' }}>
        <strong style={{ marginRight: 6, fontSize: 14 }}>建模来源</strong>
        <ClickHelp content={<>地质条件读取已发布的天然裂缝；自定义条件按当前压裂水平段自动确定建模范围。</>} label="查看建模来源说明" />
        {([
          ['GEOLOGY', '地质条件'], ['CUSTOM', '自定义条件'],
        ] as const).map(([mode, label]) => <label key={mode} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <input type="radio" checked={draft.source_mode === mode} onChange={() => setDraft((current) => ({
            ...current,
            source_mode: mode,
            custom_fractures: mode === 'CUSTOM' && current.custom_fractures.length === 0
              ? generateCustomNaturalFractures(
                current.fracture_sets,
                current.design_metadata.boundary,
                current.design_metadata.random_seed,
              )
              : current.custom_fractures,
          }))} />{label}
        </label>)}
      </div>
      <div style={{ minHeight: 0, display: 'grid', gridTemplateColumns: usesCustom ? 'minmax(650px, 1fr) minmax(520px, 1.12fr)' : 'minmax(520px, 1fr)', gap: 12, padding: 12 }}>
        {usesCustom && <div style={{ minHeight: 0, overflow: 'auto', border: '1px solid #C9D0D8', borderRadius: 5, background: '#fff' }}>
          <div style={{ padding: 12, borderBottom: '1px solid #D7DCE2', background: '#F4F6F8' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <strong style={{ fontSize: 14 }}>天然裂缝配置</strong>
              <button type="button" onClick={addSet} style={{ ...button, borderColor: '#6A4C93', color: '#5D4382' }}>＋ 新增</button>
              <ClickHelp content={<>
                <div>建模范围：{draft.design_metadata.boundary.widthM.toFixed(0)} × {draft.design_metadata.boundary.heightM.toFixed(0)} m，由当前压裂水平段自动确定。</div>
                <div>方位约定：0° = 北，90° = 东。</div>
                <div>长度、方位和间距采用截断正态采样，同一随机实现编号可复现。</div>
              </>} label="查看裂缝组参数说明" />
              <label style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>随机实现编号
                <input type="number" value={draft.design_metadata.random_seed} onChange={(event) => setDraft((current) => ({
              ...current, design_metadata: { ...current.design_metadata, random_seed: Number(event.target.value) },
                }))} style={{ ...control, width: 110 }} />
              </label>
            </div>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', fontSize: 12 }}>
            <thead><tr style={{ background: '#E9EDF1' }}>
              {['裂缝组', '再生成', '统计量', '长度 (m)', '方位 (°)', '间距 (m)'].map((label) => <th key={label} style={{ padding: '9px 6px', border: '1px solid #CDD3DA', fontWeight: 650, color: '#2B4055' }}>{label}</th>)}
            </tr></thead>
            <tbody>{draft.fracture_sets.flatMap((set) => ([
              <tr key={`${set.id}-average`}>
                <td rowSpan={2} style={{ border: '1px solid #D7DCE2', padding: 7 }}>
                  <input value={set.name} onChange={(event) => updateSet(set.id, { name: event.target.value })} style={control} />
                  <button type="button" onClick={() => setDraft((current) => ({ ...current, fracture_sets: current.fracture_sets.filter((item) => item.id !== set.id), custom_fractures: current.custom_fractures.filter((item) => item.fracture_set_id !== set.id) }))}
                    disabled={draft.fracture_sets.length === 1} style={{ ...button, marginTop: 5, width: '100%' }}>删除</button>
                </td>
                <td rowSpan={2} style={{ border: '1px solid #D7DCE2', textAlign: 'center' }}><input type="checkbox" checked={set.regenerate} onChange={(event) => updateSet(set.id, { regenerate: event.target.checked })} /></td>
                <td style={{ border: '1px solid #D7DCE2', padding: 7 }}>平均值</td>
                {(['averageLengthM', 'averageOrientationDeg', 'averageSpacingM'] as const).map((key) => <td key={key} style={{ border: '1px solid #D7DCE2', padding: 4 }}><input type="number" min={key === 'averageOrientationDeg' ? 0 : 0.1} max={key === 'averageOrientationDeg' ? 360 : undefined} value={set[key]} onChange={(event) => updateSet(set.id, { [key]: Number(event.target.value) })} style={control} /></td>)}
              </tr>,
              <tr key={`${set.id}-std`}>
                <td style={{ border: '1px solid #D7DCE2', padding: 7 }}>标准差</td>
                {(['stdLengthM', 'stdOrientationDeg', 'stdSpacingM'] as const).map((key) => <td key={key} style={{ border: '1px solid #D7DCE2', padding: 4 }}><input type="number" min={0} value={set[key]} onChange={(event) => updateSet(set.id, { [key]: Math.max(0, Number(event.target.value)) })} style={control} /></td>)}
              </tr>,
            ]))}</tbody>
          </table>
        </div>}
        <div style={{ minWidth: 0, minHeight: 0 }}>
          {usesGeology && !geologyLoading && rawGeologyFractures.length === 0
            ? <div style={{ marginBottom: 8, padding: 8, border: '1px solid #D7B69E', background: '#FFF8F1', color: '#895231' }}>当前地质条件未提供天然裂缝数据，请选择其他天然裂缝来源或返回地质条件补充天然裂缝数据。</div>
            : null}
          <NaturalFracture2DPreview design={draft} survey={survey} perforationMds={perforationMds} />
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 12px', borderTop: '1px solid #CDD3DA', background: '#F5F6F8' }}>
        <div style={{ display: 'flex', gap: 7 }}>
          <button type="button" disabled={!usesCustom} onClick={regenerate} style={{ ...button, opacity: usesCustom ? 1 : 0.5 }}>重新生成</button>
          <button type="button" onClick={() => {
            const normalized = normalizeNaturalFractureDesign(draft);
            const applied = usesCustom && normalized.custom_fractures.length === 0
              ? { ...normalized, custom_fractures: generateCustomNaturalFractures(normalized.fracture_sets, normalized.design_metadata.boundary, normalized.design_metadata.random_seed) }
              : normalized;
            onApply(cloneDesign(applied));
          }} style={{ ...button, borderColor: '#6A4C93', background: '#6A4C93', color: '#fff' }}>应用</button>
          <button type="button" onClick={onCancel} style={button}>取消</button>
        </div>
      </div>
    </div>
  </Modal>;
}
